import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  FileText, Plus, Edit, Trash2, LogOut, 
  CheckCircle, Clock, ArrowRight,
  Building, Search, BarChart3, Bell, Scale, Calendar
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';

const API = process.env.REACT_APP_BACKEND_URL;

const THEME_COLOR = '#E94B7B';

// حالات التنفيذ
const EXECUTION_STATUS = {
  filed: { label: 'تم رفع التنفيذ', color: 'bg-blue-500', icon: FileText },
  in_progress: { label: 'تحت الإجراء', color: 'bg-yellow-500', icon: Clock },
  completed: { label: 'منتهي', color: 'bg-green-500', icon: CheckCircle }
};

export default function ExecutionDashboard() {
  const navigate = useNavigate();
  const { companyId } = useParams();
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [executions, setExecutions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Dialogs
  const [isExecutionDialogOpen, setIsExecutionDialogOpen] = useState(false);
  const [editingExecution, setEditingExecution] = useState(null);
  
  // Form data
  const [executionForm, setExecutionForm] = useState({
    contract_number: '',
    payment_number: '',
    payment_amount: '',
    execution_request_number: '',
    filing_date: '',
    execution_status: 'filed',
    execution_notes: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const userRes = await axios.get(`${API}/api/auth/me`, { headers });
      setUser(userRes.data);
      
      const companiesRes = await axios.get(`${API}/api/companies`, { headers });
      const comp = companiesRes.data.find(c => c.id === companyId);
      setCompany(comp);
      
      const executionsRes = await axios.get(`${API}/api/executions?company_id=${companyId}`, { headers });
      setExecutions(executionsRes.data);
      
      const statsRes = await axios.get(`${API}/api/stats?company_id=${companyId}`, { headers });
      setStats(statsRes.data);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  }, [companyId, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateExecution = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/executions`, {
        ...executionForm,
        company_id: companyId,
        payment_amount: parseFloat(executionForm.payment_amount),
        filing_date: new Date(executionForm.filing_date).toISOString()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('تم إضافة التنفيذ بنجاح');
      setIsExecutionDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل إضافة التنفيذ');
    }
  };

  const handleUpdateExecution = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/executions/${editingExecution.id}`, {
        contract_number: executionForm.contract_number,
        payment_number: executionForm.payment_number,
        payment_amount: parseFloat(executionForm.payment_amount),
        execution_request_number: executionForm.execution_request_number,
        filing_date: new Date(executionForm.filing_date).toISOString(),
        execution_status: executionForm.execution_status,
        execution_notes: executionForm.execution_notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('تم تحديث التنفيذ بنجاح');
      setEditingExecution(null);
      setIsExecutionDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل تحديث التنفيذ');
    }
  };

  const handleDeleteExecution = async (executionId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا التنفيذ؟')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/executions/${executionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('تم حذف التنفيذ بنجاح');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل حذف التنفيذ');
    }
  };

  const resetForm = () => {
    setExecutionForm({
      contract_number: '',
      payment_number: '',
      payment_amount: '',
      execution_request_number: '',
      filing_date: '',
      execution_status: 'filed',
      execution_notes: ''
    });
  };

  const openEditDialog = (execution) => {
    setEditingExecution(execution);
    setExecutionForm({
      contract_number: execution.contract_number,
      payment_number: execution.payment_number,
      payment_amount: execution.payment_amount.toString(),
      execution_request_number: execution.execution_request_number,
      filing_date: execution.filing_date ? execution.filing_date.split('T')[0] : '',
      execution_status: execution.execution_status,
      execution_notes: execution.execution_notes || ''
    });
    setIsExecutionDialogOpen(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('ar-SA');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ar-SA', {
      style: 'currency',
      currency: 'SAR'
    }).format(amount);
  };

  // Filter executions
  const filteredExecutions = executions.filter(exec => {
    const matchesSearch = 
      exec.contract_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      exec.payment_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      exec.execution_request_number?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || exec.execution_status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const isLegalManager = user?.role === 'legal_manager';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" dir="rtl">
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-72 bg-gradient-to-b from-slate-800 to-slate-900 min-h-screen p-6 fixed right-0 top-0">
          {/* Company Logo */}
          <div className="bg-white rounded-xl p-4 mb-6">
            {company?.logo_url ? (
              <img
                src={company.logo_url}
                alt={company?.name_ar}
                className="w-full h-20 object-contain"
              />
            ) : (
              <Building size={48} className="mx-auto text-slate-400" />
            )}
            <h2 className="text-slate-800 font-bold text-center mt-2 text-sm">{company?.name_ar}</h2>
          </div>
          
          {/* Navigation */}
          <nav className="space-y-2">
            <button
              onClick={() => navigate('/companies')}
              className="w-full text-right px-4 py-2.5 rounded-md flex items-center gap-3 text-slate-300 hover:bg-slate-700 hover:text-white transition-colors"
              data-testid="back-to-companies"
            >
              <ArrowRight size={18} />
              العودة للشركات
            </button>
            
            <div className="w-full text-right px-4 py-2.5 rounded-md flex items-center gap-3 bg-[#E94B7B] text-white">
              <Scale size={18} />
              التنفيذات
            </div>
            
            {/* التقارير - للمدير القانوني فقط */}
            {user?.role === 'legal_manager' && (
              <button
                onClick={() => navigate(`/reports/${companyId}`)}
                className="w-full text-right px-4 py-2.5 rounded-md flex items-center gap-3 text-slate-300 hover:bg-slate-700 hover:text-white transition-colors"
              >
                <BarChart3 size={18} />
                التقارير
              </button>
            )}
          </nav>
          
          {/* User Info */}
          <div className="absolute bottom-6 right-6 left-6">
            <div className="bg-slate-700/50 rounded-lg p-3 mb-3">
              <p className="text-white text-sm font-medium">{user?.full_name}</p>
              <p className="text-slate-400 text-xs">
                {user?.role === 'legal_manager' ? 'مدير الشؤون القانونية' : 'الرئيس التنفيذي'}
              </p>
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-700"
              onClick={() => {
                localStorage.removeItem('token');
                navigate('/login');
              }}
            >
              <LogOut size={18} className="ml-2" />
              تسجيل الخروج
            </Button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 mr-72 p-8">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-2xl font-bold text-slate-800">إدارة التنفيذات</h1>
              <p className="text-slate-500 mt-1">شركة {company?.name_ar}</p>
            </div>
            
            {isLegalManager && (
              <Button
                onClick={() => {
                  setEditingExecution(null);
                  resetForm();
                  setIsExecutionDialogOpen(true);
                }}
                className="bg-[#E94B7B] hover:bg-[#d43d6a] text-white"
                data-testid="add-execution-btn"
              >
                <Plus size={18} className="ml-2" />
                إضافة تنفيذ جديد
              </Button>
            )}
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Scale size={20} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stats?.executions?.total || 0}</p>
                  <p className="text-sm text-slate-500">إجمالي التنفيذات</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FileText size={20} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stats?.executions?.filed || 0}</p>
                  <p className="text-sm text-slate-500">تم الرفع</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <Clock size={20} className="text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stats?.executions?.in_progress || 0}</p>
                  <p className="text-sm text-slate-500">تحت الإجراء</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle size={20} className="text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stats?.executions?.completed || 0}</p>
                  <p className="text-sm text-slate-500">منتهي</p>
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 mb-6">
            <div className="flex gap-4">
              <div className="flex-1 relative">
                <Search size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="بحث برقم العقد أو رقم الدفعة أو رقم التنفيذ..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pr-10"
                  data-testid="search-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48" data-testid="status-filter">
                  <SelectValue placeholder="حالة التنفيذ" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">جميع الحالات</SelectItem>
                  <SelectItem value="filed">تم رفع التنفيذ</SelectItem>
                  <SelectItem value="in_progress">تحت الإجراء</SelectItem>
                  <SelectItem value="completed">منتهي</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Executions Table */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600">رقم العقد</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600">رقم الدفعة</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600">مبلغ الدفعة</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600">رقم طلب التنفيذ</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600">تاريخ الرفع</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600">حالة التنفيذ</th>
                  {isLegalManager && (
                    <th className="text-center py-3 px-4 text-sm font-semibold text-slate-600">إجراءات</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {filteredExecutions.length === 0 ? (
                  <tr>
                    <td colSpan={isLegalManager ? 7 : 6} className="text-center py-12 text-slate-500">
                      <Scale size={48} className="mx-auto mb-4 opacity-30" />
                      <p>لا توجد تنفيذات</p>
                    </td>
                  </tr>
                ) : (
                  filteredExecutions.map((execution) => {
                    const status = EXECUTION_STATUS[execution.execution_status] || EXECUTION_STATUS.filed;
                    const StatusIcon = status.icon;
                    
                    return (
                      <tr 
                        key={execution.id} 
                        className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                        data-testid={`execution-row-${execution.id}`}
                      >
                        <td className="py-3 px-4 text-sm text-slate-800 font-medium">
                          {execution.contract_number}
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600">
                          {execution.payment_number}
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600">
                          {formatCurrency(execution.payment_amount)}
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600">
                          {execution.execution_request_number}
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600">
                          {formatDate(execution.filing_date)}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium text-white ${status.color}`}>
                            <StatusIcon size={12} />
                            {status.label}
                          </span>
                        </td>
                        {isLegalManager && (
                          <td className="py-3 px-4">
                            <div className="flex justify-center gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                onClick={() => openEditDialog(execution)}
                                data-testid={`edit-execution-${execution.id}`}
                              >
                                <Edit size={16} />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDeleteExecution(execution.id)}
                                data-testid={`delete-execution-${execution.id}`}
                              >
                                <Trash2 size={16} />
                              </Button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </main>
      </div>

      {/* Add/Edit Execution Dialog */}
      <Dialog open={isExecutionDialogOpen} onOpenChange={setIsExecutionDialogOpen}>
        <DialogContent className="max-w-lg" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-2xl text-right">
              {editingExecution ? 'تعديل التنفيذ' : 'إضافة تنفيذ جديد'}
            </DialogTitle>
            <DialogDescription className="text-right">
              {editingExecution ? 'عدّل بيانات التنفيذ' : 'أدخل بيانات التنفيذ الجديد'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={editingExecution ? handleUpdateExecution : handleCreateExecution} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contract_number" className="text-right block">رقم العقد *</Label>
                <Input
                  id="contract_number"
                  value={executionForm.contract_number}
                  onChange={(e) => setExecutionForm({ ...executionForm, contract_number: e.target.value })}
                  required
                  placeholder="أدخل رقم العقد"
                  className="text-right"
                  data-testid="contract-number-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="payment_number" className="text-right block">رقم الدفعة *</Label>
                <Input
                  id="payment_number"
                  value={executionForm.payment_number}
                  onChange={(e) => setExecutionForm({ ...executionForm, payment_number: e.target.value })}
                  required
                  placeholder="أدخل رقم الدفعة"
                  className="text-right"
                  data-testid="payment-number-input"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="payment_amount" className="text-right block">مبلغ الدفعة *</Label>
                <Input
                  id="payment_amount"
                  type="number"
                  step="0.01"
                  value={executionForm.payment_amount}
                  onChange={(e) => setExecutionForm({ ...executionForm, payment_amount: e.target.value })}
                  required
                  placeholder="0.00"
                  className="text-left"
                  dir="ltr"
                  data-testid="payment-amount-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="execution_request_number" className="text-right block">رقم طلب التنفيذ *</Label>
                <Input
                  id="execution_request_number"
                  value={executionForm.execution_request_number}
                  onChange={(e) => setExecutionForm({ ...executionForm, execution_request_number: e.target.value })}
                  required
                  placeholder="أدخل رقم طلب التنفيذ"
                  className="text-right"
                  data-testid="execution-request-number-input"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="filing_date" className="text-right block">تاريخ الرفع *</Label>
                <Input
                  id="filing_date"
                  type="date"
                  value={executionForm.filing_date}
                  onChange={(e) => setExecutionForm({ ...executionForm, filing_date: e.target.value })}
                  required
                  className="text-left"
                  dir="ltr"
                  data-testid="filing-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="execution_status" className="text-right block">حالة التنفيذ *</Label>
                <Select
                  value={executionForm.execution_status}
                  onValueChange={(value) => setExecutionForm({ ...executionForm, execution_status: value })}
                >
                  <SelectTrigger className="w-full text-right" data-testid="execution-status-select">
                    <SelectValue placeholder="اختر الحالة" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="filed">تم رفع التنفيذ</SelectItem>
                    <SelectItem value="in_progress">تحت الإجراء</SelectItem>
                    <SelectItem value="completed">منتهي</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="execution_notes" className="text-right block">ملاحظات</Label>
              <Textarea
                id="execution_notes"
                value={executionForm.execution_notes}
                onChange={(e) => setExecutionForm({ ...executionForm, execution_notes: e.target.value })}
                placeholder="أدخل أي ملاحظات..."
                className="text-right min-h-[80px]"
                data-testid="execution-notes-input"
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button 
                type="submit" 
                className="flex-1 bg-[#E94B7B] hover:bg-[#d43d6a]" 
                data-testid="submit-execution-btn"
              >
                {editingExecution ? 'حفظ التعديلات' : 'إضافة التنفيذ'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsExecutionDialogOpen(false);
                  setEditingExecution(null);
                  resetForm();
                }}
                className="flex-1"
              >
                إلغاء
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
